/**
 * signup-password/index.tsx  — Auth0 ACUL screen: "signup-password"
 *
 * WHAT THIS SCREEN DOES
 * ---------------------
 * Step 3 of the 3-step signup flow: "Create password".
 * Collects a new password (with confirmation + complexity rules).
 * Calls `signupPassword.signup({ password })`.
 *
 * The stepper renders with step 3 active; steps 1 and 2 are shown as done.
 *
 * BACK NAVIGATION
 * ---------------
 * Auth0 provides `editLink` on the screen context for going back to signup-id.
 * window.history.back() is the fallback.
 *
 * HOW TO EXTEND
 * -------------
 * If your tenant collects additional signup fields (e.g. username), pass
 * them here via `signupPassword.signup({ password, username })`.
 */

import { AuthLayout } from "@/components/auth/AuthLayout";
import { PasswordForm } from "@/components/auth/PasswordForm";
import { SignupStepper } from "@/components/auth/SignupStepper";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";
import { useSignupPasswordManager } from "./hooks/useSignupPasswordManager";

function SignupPasswordScreen() {
  const { signupPassword, texts, locales, editLink, handleSignupPassword } =
    useSignupPasswordManager();

  document.title =
    texts?.pageTitle || locales?.page?.title || "Create your password";
  applyAuth0Theme(signupPassword);

  const handleSubmit = async ({
    password,
    confirmPassword,
    email,
    phoneNumber,
  }: {
    password: string;
    confirmPassword?: string;
    email?: string;
    phoneNumber?: string;
  }) => {
    try {
      await handleSignupPassword({
        password,
        confirmPassword,
        email,
        phoneNumber,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleBack = () => {
    if (editLink) {
      window.location.href = editLink;
    } else {
      window.history.back();
    }
  };

  return (
    <AuthLayout
      title="Become a member"
      signupVariant
      headerSlot={<SignupStepper activeStep={3} />}
    >
      <PasswordForm
        onSubmit={handleSubmit}
        onBack={handleBack}
        isNewPassword
        submitLabel="Create account"
        signupPassword={signupPassword}
      />
    </AuthLayout>
  );
}

export default SignupPasswordScreen;
